import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Upload, FileJson, Database, AlertTriangle, CheckCircle2,
  XCircle, Loader2, ArrowRight, RotateCcw, Merge, Replace,
  Shield, Info, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const STEPS = { UPLOAD: 0, PREVIEW: 1, CONFIGURE: 2, IMPORTING: 3, DONE: 4 };

export default function ImportPage() {
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState('merge');
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setStep(STEPS.UPLOAD);
    setFile(null);
    setPreview(null);
    setMode('merge');
    setSelectedCollections([]);
    setResults(null);
  };

  const handleFile = useCallback(async (f) => {
    if (!f || !f.name.endsWith('.json')) {
      toast.error('Veuillez selectionner un fichier JSON');
      return;
    }
    setFile(f);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await axios.post('/backup/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview(res.data);
      setSelectedCollections(res.data.collections.map(c => c.collection));
      setStep(STEPS.PREVIEW);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'analyse du fichier');
      setFile(null);
    }
    setLoading(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const toggleCollection = (name) => {
    setSelectedCollections(prev =>
      prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]
    );
  };

  const toggleAll = () => {
    if (!preview) return;
    const all = preview.collections.map(c => c.collection);
    setSelectedCollections(prev => prev.length === all.length ? [] : all);
  };

  const runImport = async () => {
    if (selectedCollections.length === 0) {
      toast.error('Selectionnez au moins une collection');
      return;
    }
    setStep(STEPS.IMPORTING);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', mode);
      formData.append('collections_filter', selectedCollections.join(','));
      const res = await axios.post('/backup/import-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
      });
      setResults(res.data);
      setStep(STEPS.DONE);
      toast.success('Import termine !');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'import');
      setStep(STEPS.CONFIGURE);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-display font-bold text-[#0F172A] mb-2">
            Importation de donnees
          </h1>
          <p className="text-slate-600">
            Restaurez ou fusionnez des donnees depuis un fichier de sauvegarde FISL
          </p>
        </div>
        {step !== STEPS.UPLOAD && (
          <Button variant="outline" onClick={reset} data-testid="btn-reset-import">
            <RotateCcw className="h-4 w-4 mr-2" />Recommencer
          </Button>
        )}
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-sm overflow-x-auto pb-2">
        {['Fichier', 'Apercu', 'Configuration', 'Import', 'Resultat'].map((label, i) => (
          <div key={i} className="flex items-center gap-2 whitespace-nowrap">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
              ${step === i ? 'bg-[#0F172A] text-white' : step > i ? 'bg-[#8DC63F] text-white' : 'bg-slate-200 text-slate-500'}`}>
              {step > i ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
            </div>
            <span className={step >= i ? 'text-[#0F172A] font-medium' : 'text-slate-400'}>{label}</span>
            {i < 4 && <ArrowRight className="h-4 w-4 text-slate-300" />}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === STEPS.UPLOAD && (
        <div
          className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer
            ${dragOver ? 'border-[#8DC63F] bg-green-50' : 'border-slate-300 hover:border-slate-400 bg-white'}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="file-input"
          />
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-12 w-12 text-[#8DC63F] animate-spin" />
              <p className="text-slate-600 font-medium">Analyse du fichier en cours...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                <Upload className="h-8 w-8 text-slate-400" />
              </div>
              <div>
                <p className="text-lg font-semibold text-[#0F172A] mb-1">
                  Glissez-deposez votre fichier de sauvegarde ici
                </p>
                <p className="text-slate-500 text-sm">ou cliquez pour parcourir - Format JSON (export FISL)</p>
              </div>
              <FileJson className="h-6 w-6 text-slate-300" />
            </div>
          )}
        </div>
      )}

      {/* Step 2: Preview */}
      {step === STEPS.PREVIEW && preview && (
        <div className="space-y-4">
          {/* File info */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-wrap items-center gap-4">
            <FileJson className="h-6 w-6 text-[#0F172A]" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#0F172A] truncate">{file?.name}</p>
              <p className="text-sm text-slate-500">
                Export du {preview.export_date?.split('T')[0] || 'date inconnue'} -
                {' '}{preview.file_size_mb} MB - {preview.total_documents} documents
              </p>
            </div>
          </div>

          {/* Collections table */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-[#0F172A] text-white px-4 py-3 flex items-center justify-between">
              <span className="font-semibold flex items-center gap-2">
                <Database className="h-4 w-4" />
                Collections ({preview.collections.length})
              </span>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={toggleAll} data-testid="btn-toggle-all">
                {selectedCollections.length === preview.collections.length ? 'Tout deselectionner' : 'Tout selectionner'}
              </Button>
            </div>
            <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
              {preview.collections.map((col) => (
                <label
                  key={col.collection}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors
                    ${selectedCollections.includes(col.collection) ? 'bg-green-50/50' : ''}`}
                  data-testid={`collection-row-${col.collection}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedCollections.includes(col.collection)}
                    onChange={() => toggleCollection(col.collection)}
                    className="w-4 h-4 rounded border-slate-300 text-[#8DC63F] focus:ring-[#8DC63F]"
                    data-testid={`checkbox-${col.collection}`}
                  />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-[#0F172A]">{col.collection}</span>
                    {col.is_sensitive && (
                      <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">sensible</Badge>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 flex items-center gap-4 whitespace-nowrap">
                    <span>{col.import_count} a importer</span>
                    <span className="text-slate-300">|</span>
                    <span>{col.current_count} en base</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              className="bg-[#0F172A] hover:bg-[#1e293b] text-white"
              onClick={() => setStep(STEPS.CONFIGURE)}
              disabled={selectedCollections.length === 0}
              data-testid="btn-next-configure"
            >
              Suivant : Configuration <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Configure mode */}
      {step === STEPS.CONFIGURE && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-[#0F172A]">Mode d'importation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Merge */}
            <div
              className={`border-2 rounded-xl p-5 cursor-pointer transition-all
                ${mode === 'merge' ? 'border-[#8DC63F] bg-green-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
              onClick={() => setMode('merge')}
              data-testid="mode-merge"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${mode === 'merge' ? 'bg-[#8DC63F] text-white' : 'bg-slate-100 text-slate-500'}`}>
                  <Merge className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A]">Fusion</h3>
                  <p className="text-xs text-slate-500">Recommande</p>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                Ajoute les nouvelles donnees et met a jour les existantes.
                Les donnees actuelles non presentes dans le fichier sont conservees.
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs text-green-700 bg-green-100 rounded-md px-3 py-2">
                <Shield className="h-3.5 w-3.5" /> Aucune perte de donnees
              </div>
            </div>

            {/* Replace */}
            <div
              className={`border-2 rounded-xl p-5 cursor-pointer transition-all
                ${mode === 'replace' ? 'border-red-400 bg-red-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
              onClick={() => setMode('replace')}
              data-testid="mode-replace"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${mode === 'replace' ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  <Replace className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A]">Remplacement</h3>
                  <p className="text-xs text-slate-500">Restauration complete</p>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                Vide les collections selectionnees puis insere les donnees du fichier.
                Ideal pour une restauration apres incident.
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-100 rounded-md px-3 py-2">
                <AlertTriangle className="h-3.5 w-3.5" /> Les donnees existantes seront supprimees
              </div>
            </div>
          </div>

          {/* Warning for replace */}
          {mode === 'replace' && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800">Attention - Mode Remplacement</p>
                <p className="text-sm text-amber-700 mt-1">
                  Les {selectedCollections.length} collection(s) selectionnee(s) seront videes
                  avant l'import. Cette action est irreversible. Assurez-vous d'avoir
                  un backup recent avant de continuer.
                </p>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-4 w-4 text-slate-500" />
              <span className="font-medium text-[#0F172A]">Resume</span>
            </div>
            <ul className="text-sm text-slate-600 space-y-1 ml-6">
              <li>Mode : <strong>{mode === 'merge' ? 'Fusion' : 'Remplacement'}</strong></li>
              <li>Collections : <strong>{selectedCollections.length}</strong> selectionnee(s)</li>
              <li>Documents a traiter : <strong>
                {preview?.collections
                  .filter(c => selectedCollections.includes(c.collection))
                  .reduce((sum, c) => sum + c.import_count, 0) || 0}
              </strong></li>
            </ul>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(STEPS.PREVIEW)} data-testid="btn-back-preview">
              Retour
            </Button>
            <Button
              className={mode === 'replace' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-[#8DC63F] hover:bg-[#7ab535] text-white'}
              onClick={runImport}
              data-testid="btn-start-import"
            >
              {mode === 'replace' ? 'Remplacer et importer' : 'Fusionner et importer'}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Importing */}
      {step === STEPS.IMPORTING && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Loader2 className="h-16 w-16 text-[#8DC63F] animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#0F172A] mb-2">Import en cours...</h2>
          <p className="text-slate-500">
            Traitement de {selectedCollections.length} collection(s). Ne fermez pas cette page.
          </p>
        </div>
      )}

      {/* Step 5: Results */}
      {step === STEPS.DONE && results && (
        <div className="space-y-4">
          {/* Summary banner */}
          <div className={`rounded-xl p-6 flex items-center gap-4 ${results.total_errors > 0 ? 'bg-amber-50 border border-amber-300' : 'bg-green-50 border border-green-300'}`}>
            {results.total_errors > 0 ? (
              <AlertTriangle className="h-8 w-8 text-amber-600 flex-shrink-0" />
            ) : (
              <CheckCircle2 className="h-8 w-8 text-green-600 flex-shrink-0" />
            )}
            <div>
              <h2 className="text-lg font-bold text-[#0F172A]">
                Import {results.total_errors > 0 ? 'termine avec des erreurs' : 'reussi'}
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Mode : {results.mode === 'merge' ? 'Fusion' : 'Remplacement'} |
                {' '}{results.total_inserted} insere(s) | {results.total_updated} mis a jour |
                {' '}{results.total_errors} erreur(s)
              </p>
            </div>
          </div>

          {/* Detail per collection */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-[#0F172A] text-white px-4 py-3 font-semibold">
              Detail par collection
            </div>
            <div className="divide-y divide-slate-100">
              {results.details.map((r) => (
                <div key={r.collection} className="flex items-center gap-3 px-4 py-3" data-testid={`result-${r.collection}`}>
                  {r.status === 'error' ? (
                    <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  ) : r.status === 'skipped' ? (
                    <div className="h-5 w-5 rounded-full bg-slate-200 flex-shrink-0" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  )}
                  <span className="font-medium text-[#0F172A] flex-1">{r.collection}</span>
                  <div className="text-sm text-slate-500">
                    {r.status === 'replaced' && (
                      <span>{r.deleted} supprime(s), {r.inserted} insere(s)</span>
                    )}
                    {r.status === 'merged' && (
                      <span>{r.inserted} insere(s), {r.updated} mis a jour</span>
                    )}
                    {r.status === 'skipped' && (
                      <span className="text-slate-400">{r.reason}</span>
                    )}
                    {r.status === 'error' && (
                      <span className="text-red-500">{r.error}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={reset} data-testid="btn-new-import">
              <RotateCcw className="h-4 w-4 mr-2" />Nouvel import
            </Button>
            <Button
              className="bg-[#0F172A] hover:bg-[#1e293b] text-white"
              onClick={() => window.location.href = '/exports'}
              data-testid="btn-go-exports"
            >
              <Download className="h-4 w-4 mr-2" />Aller aux exports
            </Button>
          </div>
        </div>
      )}

      {/* Info box */}
      {step === STEPS.UPLOAD && (
        <div className="bg-[#0F172A] text-white rounded-lg p-5 flex items-start gap-4">
          <Database className="h-8 w-8 text-[#8DC63F] flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-semibold text-lg mb-1">Comment ca marche ?</h2>
            <ul className="text-slate-300 text-sm space-y-1">
              <li>1. Exportez vos donnees depuis Exports ou Backup (format JSON FISL)</li>
              <li>2. Deposez le fichier ci-dessus pour voir l'apercu</li>
              <li>3. Selectionnez les collections et le mode (fusion ou remplacement)</li>
              <li>4. Lancez l'import - un rapport detaille sera affiche</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
